import { useCallback, useEffect, useRef, useState } from 'react'

/** Show the jump-to-bottom button once scrolled this far up from the bottom. */
const SHOW_THRESHOLD_PX = 240

/**
 * Keeps a scroll container pinned to the bottom on mount and exposes a
 * "jump to bottom" affordance once the user scrolls far enough up.
 *
 * `pinOnMount` is false when the view opens somewhere other than its tail — an
 * anchored thread (a search hit) owns its own scroll position, and a cached
 * first paint would otherwise be yanked to the bottom before it can land on it.
 */
export function useScrollToBottom<T extends HTMLElement>(pinOnMount = true) {
  const ref = useRef<T>(null)
  const [showButton, setShowButton] = useState(false)
  const pinRef = useRef(pinOnMount)
  pinRef.current = pinOnMount

  useEffect(() => {
    const el = ref.current
    if (el && pinRef.current) el.scrollTop = el.scrollHeight
  }, [])

  const handleScroll = useCallback(() => {
    const el = ref.current
    if (!el) return
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    setShowButton(distanceFromBottom > SHOW_THRESHOLD_PX)
  }, [])

  const scrollToBottom = useCallback(() => {
    const el = ref.current
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
  }, [])

  return { ref, showButton, handleScroll, scrollToBottom }
}
